import React, { useEffect, useState, useRef } from "react";
import { api, wsUrl } from "@/lib/api";
import { getRank } from "@/lib/ranks";
import { Trophy, Flame, Swords, Crown, Zap, Award, Target, Timer, Users } from "lucide-react";
import { LogoIcon } from "@/components/Logo";
import { QRCodeSVG } from "qrcode.react";

const TV_BG = "https://static.prod-images.emergentagent.com/jobs/4227fcb0-9007-41d7-b868-6b87001c3b63/images/a5ba7c42d283ac621d01821dd579fb71fcb277cf3ae28409c40c50f244cc6a8d.png";

const TV_FX_STYLES = {
  neon: { color: "#22d3ee", textShadow: "0 0 6px #22d3ee, 0 0 12px #22d3ee" },
  fire: { color: "#fb923c", textShadow: "0 0 6px #ef4444, 0 0 14px #f97316" },
  gold: { color: "#fde047", textShadow: "0 0 6px #ca8a04, 0 0 12px #facc15" },
  rainbow: { background: "linear-gradient(90deg,#ef4444,#f59e0b,#84cc16,#06b6d4,#a855f7)", WebkitBackgroundClip: "text", color: "transparent" },
  electric: { color: "#a5f3fc", textShadow: "0 0 5px #06b6d4, 0 0 12px #38bdf8" },
};

export default function TVDisplay() {
  const [snap, setSnap] = useState({ top10: [], live: [], recent: [], mvp: null });
  const [arena, setArena] = useState([]);
  const [session, setSession] = useState(null);
  const [checkins, setCheckins] = useState([]);
  const [toast, setToast] = useState(null);
  const [tick, setTick] = useState(0);
  const wsRef = useRef(null);
  const toastTimer = useRef(null);

  const loadSnap = () => api.get("/tv/snapshot").then(({ data }) => setSnap(data)).catch(() => {});
  const loadArena = () => api.get("/arena/requests").then(({ data }) => setArena(data)).catch(() => {});
  const loadSession = () => api.get("/tv/session").then(({ data }) => setSession(data)).catch(() => {});
  const loadCheckins = () => api.get("/tv/checkins").then(({ data }) => setCheckins(data)).catch(() => {});

  useEffect(() => {
    loadSnap(); loadArena(); loadSession(); loadCheckins();
    const interval = setInterval(() => { loadSnap(); loadArena(); loadCheckins(); }, 8000);
    const sessInt = setInterval(loadSession, 20000);
    const tickInt = setInterval(() => setTick((x) => x + 1), 1000);

    let ws;
    try {
      ws = new WebSocket(wsUrl("/api/ws/tv"));
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try {
          const ev = JSON.parse(e.data);
          if (["match.finish", "match.start", "rank.up"].includes(ev.type)) loadSnap();
          if (["arena.new", "arena.matched", "arena.cancelled", "arena.interest"].includes(ev.type)) loadArena();
          if (ev.type === "tv.checkin") loadCheckins();
          if (ev.type === "tv.refresh" || ev.type === "tv.mode") loadSession();
          if (ev.type === "match.finish" && ev.match?.winner_nickname) {
            showToast({ kind: "win", title: `${ev.match.winner_nickname} thắng!`, sub: `Bàn ${ev.match.table_number} · BO${ev.match.best_of}`, delta: ev.match.winner_id === ev.match.player_a_id ? ev.match.elo_delta_a : ev.match.elo_delta_b });
          }
          if (ev.type === "rank.up") {
            showToast({ kind: "rankup", title: `${ev.nickname} LÊN RANK!`, sub: `${ev.from} → ${ev.to}` });
          }
          if (ev.type === "tv.checkin" && ev.checkin?.nickname) {
            showToast({ kind: "checkin", title: `${ev.checkin.nickname}`, sub: `Vừa vào sảnh · ${ev.checkin.rank}` });
          }
        } catch {}
      };
    } catch {}
    return () => { clearInterval(interval); clearInterval(tickInt); clearInterval(sessInt); if (ws) ws.close(); };
  }, []);

  const showToast = (t) => {
    setToast(t);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 6000);
  };

  return (
    <div className="min-h-screen w-screen bg-[#020208] text-white overflow-hidden relative">
      <div className="absolute inset-0 opacity-30" style={{ backgroundImage: `url(${TV_BG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#020208]/85 via-[#020208]/70 to-[#020208]/95" />
      <div className="absolute inset-0 bg-grid opacity-30" />

      <div className="relative z-10 h-screen flex flex-col p-6 lg:p-8">
        {/* Header */}
        <header className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <LogoIcon size={56} />
            <div>
              <div className="font-display font-black text-3xl lg:text-4xl tracking-tight bg-gradient-to-br from-yellow-200 via-yellow-400 to-amber-600 bg-clip-text text-transparent" data-testid="tv-title">ĐẤU TRƯỜNG BILLIARDS</div>
              <div className="text-xs lg:text-sm text-amber-400/80 tracking-[0.4em]">RANK SYSTEM · LIVE</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-white/40">Đang trực tiếp</div>
            <div className="flex items-center gap-2 justify-end mt-1">
              <span className="relative flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 pulse-ring" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
              </span>
              <span className="font-display font-bold text-lg">{snap.live.length} TRẬN</span>
            </div>
          </div>
        </header>

        {/* Main grid */}
        <div className="flex-1 grid grid-cols-12 gap-4 lg:gap-6 min-h-0">
          {/* Leaderboard */}
          <section className="col-span-5 rounded-2xl bg-black/50 backdrop-blur-xl border border-white/8 p-5 flex flex-col min-h-0">
            <div className="flex items-center gap-2 mb-4">
              <Crown className="text-yellow-400" />
              <h2 className="font-display font-bold text-xl uppercase tracking-wider">Top 10</h2>
            </div>
            <div className="flex-1 overflow-hidden space-y-2" data-testid="tv-leaderboard">
              {snap.top10.map((p, i) => {
                const r = getRank(p.elo);
                const isPodium = i < 3;
                return (
                  <div key={p.id} className={`flex items-center gap-3 p-2.5 rounded-lg ${isPodium ? "bg-white/[0.05]" : "bg-white/[0.02]"} border border-white/5`}>
                    <div className="w-7 text-center font-display font-black text-lg" style={{ color: i === 0 ? "#facc15" : i === 1 ? "#cbd5e1" : i === 2 ? "#fb923c" : "#8d92a3" }}>{i + 1}</div>
                    <img src={p.avatar_url} alt="" className="w-9 h-9 rounded-full border" style={{ borderColor: r.color + "88" }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-base truncate">{p.nickname}</div>
                      <div className="text-xs" style={{ color: r.color }}>{r.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-black text-xl" style={{ color: r.color }}>{p.elo}</div>
                      {p.win_streak > 0 && <div className="text-[10px] text-red-400 flex items-center gap-0.5 justify-end"><Flame size={10} /> x{p.win_streak}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Center: Live matches + MVP */}
          <section className="col-span-4 flex flex-col gap-4 min-h-0">
            <div className="rounded-2xl bg-black/50 backdrop-blur-xl border border-white/8 p-5 flex-1 min-h-0 flex flex-col">
              <div className="flex items-center gap-2 mb-4">
                <Swords className="text-cyan-400" />
                <h2 className="font-display font-bold text-xl uppercase tracking-wider">Đang đấu</h2>
              </div>
              {snap.live.length === 0 ? (
                <div className="flex-1 grid place-items-center text-white/30 text-sm">Không có trận nào đang diễn ra</div>
              ) : (
                <div className="space-y-3 overflow-hidden" data-testid="tv-live">
                  {snap.live.slice(0, 4).map((m) => (
                    <div key={m.id} className="rounded-xl bg-gradient-to-br from-cyan-500/10 to-purple-500/5 border border-cyan-400/25 p-3">
                      <div className="text-[10px] uppercase tracking-widest text-cyan-300 mb-2 flex items-center gap-1"><Zap size={10} /> Bàn {m.table_number} · BO{m.best_of}</div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 text-right">
                          <div className="font-display font-bold truncate">{m.player_a_nickname}</div>
                          <div className="text-xs" style={{ color: getRank(m.player_a_elo).color }}>{m.player_a_rank}</div>
                        </div>
                        <div className="font-display font-black text-2xl text-white/40">VS</div>
                        <div className="flex-1">
                          <div className="font-display font-bold truncate">{m.player_b_nickname}</div>
                          <div className="text-xs" style={{ color: getRank(m.player_b_elo).color }}>{m.player_b_rank}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {snap.mvp && (
              <div className="rounded-2xl bg-gradient-to-br from-yellow-500/15 to-amber-700/5 border border-yellow-400/40 p-4 flex items-center gap-4">
                <Award size={36} className="text-yellow-400" />
                <div className="flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-yellow-300">MVP hôm nay</div>
                  <div className="font-display font-bold text-xl">{snap.mvp.nickname}</div>
                  <div className="text-xs text-white/60">{snap.mvp.wins_today} chiến thắng</div>
                </div>
                <img src={snap.mvp.avatar_url} alt="" className="w-14 h-14 rounded-full border-2 border-yellow-400" />
              </div>
            )}
          </section>

          {/* Right: Arena + QR + Recent results */}
          <section className="col-span-3 flex flex-col gap-4 min-h-0">
            {/* Đang tìm đối thủ */}
            <div className="rounded-2xl bg-black/50 backdrop-blur-xl border border-amber-400/30 p-4 flex flex-col min-h-0 flex-1">
              <div className="flex items-center gap-2 mb-3">
                <Target className="text-amber-300" size={18} />
                <h2 className="font-display font-bold text-base uppercase tracking-wider">Đang tìm đối thủ</h2>
              </div>
              {arena.length === 0 ? (
                <div className="text-white/30 text-xs flex-1 grid place-items-center">Chưa có ai</div>
              ) : (
                <div className="space-y-1.5 overflow-hidden" data-testid="tv-arena">
                  {arena.slice(0, 5).map((a) => {
                    const r = getRank(a.creator_elo);
                    const diff = Math.max(0, Date.now() - new Date(a.started_search_at).getTime());
                    const mm = Math.floor(diff / 60000).toString().padStart(2, "0");
                    const ss = Math.floor((diff % 60000) / 1000).toString().padStart(2, "0");
                    return (
                      <div key={a.id} className="flex items-center gap-2 p-1.5 rounded bg-amber-400/5 border border-amber-400/15">
                        <img src={a.creator_avatar} alt="" className="w-7 h-7 rounded-full" />
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-semibold text-xs truncate">{a.creator_nickname}</div>
                          <div className="text-[9px]" style={{ color: r.color }}>{a.creator_rank} · BO{a.best_of} {a.mode === "rank" ? "Rank" : "GH"}</div>
                        </div>
                        <div className="text-amber-300 font-mono-num text-xs flex items-center gap-0.5"><Timer size={9} /> {mm}:{ss}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* QR Check-in — opens on the apex (player) domain */}
            <div className="rounded-2xl bg-white p-3 flex flex-col items-center">
              <div className="text-[10px] uppercase tracking-widest text-black/60 font-display font-bold mb-1.5">Quét QR để vào sảnh</div>
              <QRCodeSVG value={`${window.location.protocol}//${window.location.hostname.replace(/^(admin|tv|app)\./, "")}/tv/checkin?t=${session?.qr_token || ""}`} size={110} level="M" />
              <div className="text-[9px] text-black/50 mt-1">Tên sẽ hiện lên TV</div>
            </div>

            {/* Sảnh chờ — check-ins */}
            <div className="rounded-2xl bg-black/50 backdrop-blur-xl border border-emerald-400/30 p-3 flex flex-col min-h-0 flex-1">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5">
                  <Users size={14} className="text-emerald-300" />
                  <h2 className="font-display font-bold text-xs uppercase tracking-wider">Sảnh chờ</h2>
                </div>
                <span className="text-[10px] text-emerald-300">{checkins.length}</span>
              </div>
              {checkins.length === 0 ? (
                <div className="text-white/30 text-[10px] flex-1 grid place-items-center">Quét QR để vào</div>
              ) : (
                <div className="space-y-1 overflow-hidden" data-testid="tv-checkins">
                  {checkins.slice(0, 6).map((c) => {
                    const fxStyle = TV_FX_STYLES[c.tv_effect] || {};
                    return (
                      <div key={c.id} className="flex items-center gap-2 p-1 rounded bg-emerald-400/5 border border-emerald-400/15">
                        <div className="rounded-full p-[2px]" style={{ background: c.avatar_frame || "transparent" }}>
                          <img src={c.avatar_url} alt="" className="w-6 h-6 rounded-full" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-bold text-[11px] truncate" style={fxStyle}>{c.nickname}</div>
                          {c.title_text && <div className="text-[8px] text-amber-300 truncate">{c.title_text}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Toast overlay */}
      {toast && (
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 animate-float-up pointer-events-none">
          <div className={`rounded-2xl border-2 px-12 py-8 backdrop-blur-2xl ${toast.kind === "rankup" ? "border-red-500/60 bg-red-500/10" : "border-cyan-400/60 bg-cyan-400/10"}`}
               style={{ boxShadow: `0 0 80px ${toast.kind === "rankup" ? "rgba(239,68,68,0.6)" : "rgba(0,229,255,0.6)"}` }}>
            <div className="text-center">
              <div className="font-display font-black text-5xl lg:text-6xl mb-2 text-shadow-neon" style={{ color: toast.kind === "rankup" ? "#ef4444" : "#00e5ff" }}>
                {toast.title}
              </div>
              <div className="text-xl text-white/80">{toast.sub}</div>
              {toast.delta != null && (
                <div className="mt-3 font-display font-bold text-2xl text-cyan-300">+{toast.delta} ELO</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
